import React, { ReactElement, useCallback, useContext, useState } from 'react';
import { Button, Spinner, IconRecordActive, alertColors } from '@seismic/mantle';
import { IRecordingState, RecordingStateActions, UploadStatus, WorkflowSteps } from '../../models';
import { IRecordingStateReducerAction } from '../../hooks';
import { GatewayContext, LoggingContext } from '../../context';
import { Upload } from 'tus-js-client';
import styles from '../Footer.scss';
import { useTranslation } from 'react-i18next';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';

interface Props {
  onCancel: () => void;
  onUploadComplete: (video: Blob | null) => void;
  onProcessingComplete?: (content: string | null | Error) => void;
  state: IRecordingState;
  dispatch: React.Dispatch<IRecordingStateReducerAction>;
}

export default function PreviewFooter(props: Props): ReactElement {
  const { t } = useTranslation();
  const { onUploadComplete, state, dispatch } = props;
  const logger = useContext(LoggingContext);
  const [isDiscardOpen, setIsDiscardOpen] = useState<boolean>(false);
  const [isRedoOpen, setIsRedoOpen] = useState<boolean>(false);
  const { gatewayUrl, getAuthToken, canUseGatewayApi } = useContext(GatewayContext);
  const [percentComplete, setPercentComplete] = useState('0');

  const processVideo = useCallback(
    async (id: string) => {
      if (!canUseGatewayApi) {
        throw new Error(
          'Cannot process video because the component is missing the appropriate gateway URL configuration'
        );
      }

      if (!state.targetFolder) {
        throw new Error('Cannot process video because the supplied folder id is invalid');
      }

      var result = await fetch(`${gatewayUrl}/api/v2/process/start/${id}`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ ...state.targetFolder })
      });

      if (!result.ok) {
        logger.logError('Error {error} processing video', [result.statusText]);
        throw new Error('Error processing video');
      }

      return await result.text();
    },
    [gatewayUrl, getAuthToken, canUseGatewayApi, logger, state.targetFolder]
  );

  async function upload() {
    const video = state.recorder.blob;
    if (!video) {
      logger.logError('Attempted to upload video, however it was null');
      throw Error('Attempted to upload video, however it was null');
    }

    // Required props not passed into component, just return the video
    if (!canUseGatewayApi) {
      onUploadComplete(video);
      // eslint-disable-next-line
      props.onProcessingComplete && props.onProcessingComplete(null);
      return;
    }

    if (video) {
      // console.log('videoBlob', video.);
      const upload = new Upload(video, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        },
        chunkSize: 15000000, // Seems like our nginx proxy max size is currently set to 20MB (This can be overridden, but I'm not doing that right now)
        endpoint: `${gatewayUrl}/api/v1/upload`,
        retryDelays: [0, 3000, 5000, 10000],
        metadata: {
          filename: state.fileName ?? 'default',
          filetype: video.type
        },
        onBeforeRequest: function (req) {
          var xhr = req.getUnderlyingObject();
          xhr.withCredentials = true;
        },
        onError: function (error) {
          dispatch({ type: RecordingStateActions.CompleteUpload, payload: false });
          logger.logError('Failed to upload video to server with error {Error}', [error]);
        },
        onProgress: function (bytesUploaded, bytesTotal) {
          const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
          logger.logInfo('BytesUploaded {BytesUploaded}, BytesTotal {BytesTotal}, Percentage {Percentage}', [
            bytesUploaded,
            bytesTotal,
            percentage + '%'
          ]);

          setPercentComplete(percentage);
        },
        onSuccess: async function () {
          logger.logInfo('Upload complete');
          const id = upload.url?.substring(upload.url.lastIndexOf('/') + 1);

          // If the id cannot be parsed, we cannot process the video
          if (!id) {
            logger.logError('Failed to parse id from url {Url}', [upload.url]);
            dispatch({ type: RecordingStateActions.CompleteUpload, payload: false });
            return;
          }

          dispatch({ type: RecordingStateActions.CompleteUpload, payload: true });
          setTimeout(() => {
            onUploadComplete(video);
          }, 1000);
          try {
            const content = await processVideo(id);
            // eslint-disable-next-line
            props.onProcessingComplete && props.onProcessingComplete(content);
          } catch (err: any) {
            // eslint-disable-next-line
            props.onProcessingComplete && props.onProcessingComplete(new Error(err));
          }
        }
      });

      // Check if there are any previous uploads to continue.
      upload.findPreviousUploads().then(function (previousUploads) {
        // Found previous uploads so we select the first one.
        if (previousUploads.length) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }

        // Start the upload
        dispatch({ type: RecordingStateActions.StartUpload });
        upload.start();
      });
    }
  }

  const onDiscardOpen = () => setIsDiscardOpen(true);
  const onDiscardAccept = () => {
    setIsDiscardOpen(false);
    props.onCancel();
  };
  const onDiscardReject = () => setIsDiscardOpen(false);

  const onRedoOpen = () => setIsRedoOpen(true);
  const onRedoAccept = () => {
    setIsRedoOpen(false);
    dispatch({ type: RecordingStateActions.SetTargetFolder, payload: undefined });
    dispatch({ type: RecordingStateActions.ChangeStep, payload: WorkflowSteps.Setup });
  };
  const onRedoReject = () => setIsRedoOpen(false);

  const getSaveLabel = () => {
    switch (state.uploadStatus) {
      case UploadStatus.Uploading:
        return t('Preview.Footer.Saving', { defaultValue: 'Saving' });
      case UploadStatus.Success:
        return t('Preview.Footer.Saved', { defaultValue: 'Saved' });
      default:
        return t('Preview.Footer.Save', { defaultValue: 'Save' });
    }
  };
  const didUploadSucceed = state.uploadStatus === UploadStatus.Success;
  const hasValidFileName = state.fileName !== undefined && state.fileName !== '';
  const hasSelectedFolder = state.targetFolder !== undefined;
  const isSavedDisabled =
    !hasValidFileName ||
    !hasSelectedFolder ||
    state.uploadStatus === UploadStatus.Uploading ||
    state.uploadStatus === UploadStatus.Success;

  return (
    <>
      <div className={styles.footer} data-testid="previewFooter">
        <Button
          label={t('Preview.Footer.Redo', { defaultValue: 'Redo' })}
          className={styles.redoButton}
          data-testid="redoButton"
          startAdornment={<IconRecordActive className={styles.recordIcon} />}
          variant="secondary"
          onClick={onRedoOpen}
        />
        <Button
          label={t('Footer.Discard', { defaultValue: 'Discard' })}
          className={styles.discardButton}
          data-testid="discardButton"
          variant="tertiary"
          onClick={onDiscardOpen}
        />
        <Button
          data-testid="saveButton"
          // TODO why does adding className doesn't override colors?
          // override colors so button is disabled but still shows the green success colors
          style={{
            backgroundColor: didUploadSucceed ? alertColors.success : undefined,
            borderColor: didUploadSucceed ? alertColors.success : undefined,
            color: didUploadSucceed ? 'white' : undefined
          }}
          label={getSaveLabel()}
          disabled={isSavedDisabled}
          startAdornment={
            state.uploadStatus === UploadStatus.Uploading ? (
              percentComplete === '0' ? (
                <Spinner data-testid="uploadingSpinner" size={16} />
              ) : (
                `(${percentComplete}%)`
              )
            ) : null
          }
          variant="primary"
          onClick={upload}
        />
      </div>
      <DeleteConfirmationModal
        isOpen={isRedoOpen}
        header={t('Preview.Modal.Redo.Header', { defaultValue: 'Redo Recording?' })}
        description={t('Preview.Modal.Redo.Description', {
          defaultValue: 'Your current recording will not be saved.'
        })}
        acceptButtonText={t('Preview.Modal.Redo.Accept', { defaultValue: 'Redo Recording' })}
        onAccept={onRedoAccept}
        onReject={onRedoReject}
      />
      <DeleteConfirmationModal
        isOpen={isDiscardOpen}
        header={t('Modal.Discard.Header', { defaultValue: 'Discard Recording?' })}
        description={t('Modal.Discard.Description', {
          defaultValue: 'Your current recording will not be saved and the recorder will close.'
        })}
        acceptButtonText={t('Footer.Discard', { defaultValue: 'Discard' })}
        onAccept={onDiscardAccept}
        onReject={onDiscardReject}
      />
    </>
  );
}
